import { useUserStore } from '../stores/user';

// Generic API envelope returned by the backend (`{ data, meta }`). Shared because nearly every
// endpoint uses it; pagination fields are optional since they vary per endpoint.
export interface Pagination {
    total?: number
    per_page?: number
    current_page?: number
    total_pages?: number
    last_page?: number
}

export interface DataResult<T> {
    data: T
    meta?: { pagination?: Pagination }
}

// Unwraps a nested `{ data: … }` envelope (Fractal serializes included Item resources this way, e.g.
// the organization profile's `theme`). Defensive: returns an already-flat value unchanged, so it is
// safe to apply even when the field is not wrapped. Mirrors Angular's `mapDataResult`.
export function unwrap<T>(value: DataResult<T> | T | null | undefined): T | undefined {
    if (value != null && typeof value === 'object' && 'data' in value) {
        return (value as DataResult<T>).data;
    }
    return value ?? undefined;
}

export class ApiError extends Error {
    readonly response: Response;

    constructor(response: Response) {
        super(`HTTP ${response.status} ${response.url}`);
        this.response = response;
    }
}

export const localeMapping: Record<string, string> = {
    de: 'de_DE',
    en: 'en_GB',
    es: 'es_ES',
    tr: 'tr_TR',
    uk: 'uk_UA',
};

// Active short locale ('de' | 'en' | …), kept in sync with the i18n locale via setApiLocale()
// (called from loadLocale). Single source of truth for the API `lang=` param — works for both
// the web build (path-based locale) and native (hash routing), where the old
// /mitarbeiter/<lang>/ path match never hit and silently pinned the API to German.
let currentLocale = 'de';

export function setApiLocale(locale: string): void {
    if (locale) {
        currentLocale = locale;
    }
}

function getApiLang(): string {
    return localeMapping[currentLocale] ?? 'de_DE';
}

function withLang(path: string): string {
    if (path.includes('lang=')) {
        return path;
    }
    return `${path + (path.includes('?') ? '&' : '?')}lang=${getApiLang()}`;
}

// Ein EXPLIZIT uebergebener Authorization-Header gewinnt gegen den Store-Token.
//
// Angular kennt denselben Fall: `MemberRegistrationService` schickt an
// `GET /member-application/configuration/clubs/{id}` und `POST /member-application` bewusst
// `Authorization: none` — beide Endpunkte sind `#[NoSecurity]`, und im Wizard liegt nach dem
// ersten POST ein Token vor, das dort nichts zu suchen hat. Ohne diese Zeile ueberschriebe der
// Store-Token ihn bei JEDEM Reload im laufenden Flow.
//
// Zweiter Nutznieser: `KgrTwoFactorVerify.vue` uebergibt einen SPEZIFISCHEN `twoFactorToken` —
// der wurde bisher vom Store-Token verdraengt, sobald einer gesetzt war.

/**
 * Port von Angulars `ApiAcceptVersionInterceptor`: JEDER API-Aufruf traegt den Accept-Header mit der
 * API-Version.
 *
 * Das Backend liest die Version ausschliesslich daraus (`VersionProvider::provideVersion()` parst
 * `getAcceptableContentTypes()`), und ein versionierter Endpunkt antwortet ohne ihn mit
 * **400 „Api version must not be 0"** — nicht mit einem Default. Bislang fehlte der Header im
 * shared Client; die vier zuerst migrierten Apps rufen zufaellig keinen versionierten Endpunkt auf,
 * `GET /member-application/configuration/clubs/{id}` (club-member-registration) schon.
 *
 * Ein explizit uebergebener Accept-Header gewinnt, aus demselben Grund wie bei Authorization.
 */
const API_ACCEPT_VERSION = 'application/vnd.speicherhafen.api.v2+json';

function setApiVersionHeader(headers: Headers): void {
    if (!headers.has('Accept')) {
        headers.set('Accept', API_ACCEPT_VERSION);
    }
}

function buildApiHeaders(extra?: Record<string, string>): Headers {
    const { token } = useUserStore();
    const headers = new Headers(extra);
    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Accept-Language', navigator.language);
    headers.set('X-App-Version', window.kgrDynamicConfig.appVersion);
    setApiVersionHeader(headers);
    return headers;
}

export function apiUrl(path: string): string {
    return `${window.kgrDynamicConfig.apiHost}${path}`;
}

export async function apiFetchUrl(url: string, init: RequestInit = {}): Promise<Response> {
    const headers = buildApiHeaders(init.headers as Record<string, string>);
    const response = await fetch(url, { ...init, headers });
    if (!response.ok) {
        throw new ApiError(response);
    }
    return response;
}

export async function apiFetchBlob(path: string): Promise<Blob> {
    const { token } = useUserStore();
    const { apiHost, apiPath } = window.kgrDynamicConfig;
    const headers = new Headers();
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Accept-Language', navigator.language);
    const response = await fetch(`${apiHost}${apiPath}${withLang(path)}`, { headers });
    if (!response.ok) {
        throw new ApiError(response);
    }
    return response.blob();
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    const { token } = useUserStore();
    const { apiHost, apiPath } = window.kgrDynamicConfig;

    const headers = new Headers(init.headers);
    // Explizit uebergebener Header gewinnt — siehe Kommentar an buildApiHeaders().
    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Accept-Language', navigator.language);
    headers.set('X-App-Version', window.kgrDynamicConfig.appVersion);
    setApiVersionHeader(headers);
    if (typeof init.body === 'string' && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(`${apiHost}${apiPath}${withLang(path)}`, { ...init, headers });

    if (response.status === 403) {
        const body = await response.json().catch(() => null);
        if (body?.error === 'two_factor_setup_required') {
            // 2FA setup was enforced mid-session (any guarded call 403s). Bounce to the setup page.
            // Native builds use hash routing (createWebHashHistory) — the route lives in the hash,
            // not the path — so a path-based `location.href` would load a non-existent path and blank
            // the webview. Branch on the same `window.kgrCapacitor` flag the router uses.
            const isNative = !!window.kgrCapacitor;
            const currentRoute = isNative ? window.location.hash : window.location.pathname;
            if (!currentRoute.includes('preferences/two-factor-auth')) {
                const target = `${currentLocale}/preferences/two-factor-auth`;
                if (isNative) {
                    // Change the hash and let vue-router navigate in place (no reload); throw so the
                    // caller's error handling / finally blocks still run.
                    window.location.hash = `#/${target}`;
                } else {
                    // History routing: full reload to the 2FA route under the app's base href.
                    window.location.href = `${import.meta.env.BASE_URL}${target}`;
                    await new Promise(() => {}); // reload imminent — never let the caller continue
                }
            }
            throw new ApiError(response);
        }
    }

    // Server rejected the token (Angular JWTTokenInterceptor): drop it so the router guard shows
    // the re-auth overlay on the next guarded navigation. Body left untouched for the caller's
    // ApiError handling (parseValidationErrors reads e.response.clone().json() on a 400 — note that
    // the 403 branch above already consumed the body, so cloning only works for other statuses).
    if (response.status === 401) {
        useUserStore().clearToken();
    }

    if (!response.ok) {
        throw new ApiError(response);
    }
    if (response.status === 204) {
        return undefined as T;
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
}
